import type { ShareTask } from '@egr/xbox/share/ShareTask';
import type { ShareContent } from '@egr/xbox/share/ShareManager';

import bind from 'bind-decorator';
import i18next from 'i18next';

import BaseApp from '../App';

import { EaiwsGeometryExportSettingToOption, EaiwsGeometryExportSetting } from '@egr/xbox/utils/GeometryExportFormats';
import { EaiwsImageExportSetting, EaiwsImageExportSettingToOption } from '@egr/xbox/utils/ImageExportFormats';
import { GeometryExportFormat, FileFormats, FileFormatInfo, GeometryExportFormatKey, ImageExportFormatKey, ImageExportFormat } from '@egr/xbox/utils/FileFormats';
import { HashFileInfo } from '@egr/xbox/app-api/Filesystem/Wrapper';
import { ShareContentSource, ShareManager } from '@egr/xbox/share/ShareManager';
import { assertNotNull, assert } from '@egr/xbox/utils/Debug';
import { captureException } from '@egr/xbox/utils/Error';
import { getArticleItem } from '@egr/xbox/egr-gui-utils/BasketHelper';
import { launchIOSQuicklook, launchAndroidModelViewer } from '@egr/xbox/utils/AR';
import { withProgress } from '@egr/xbox/progress-manager/ProgressManager';

import { ArticleElement, ArticleManager, MainArticleElement, UserArticleElement } from '@egr/wcf/modules/cf';
import { ArticleItem } from '@egr/wcf/modules/bsk';
import { CustomError } from '@egr/wcf/modules/utils/error';
import { DeleteItemsOptions, UpdateBasketArticlesOptions, OFMLUpdateState, UpdateBasketArticleResult, ItemSelectionOptions, ItemProperties } from '@egr/wcf/modules/eaiws/basket';
import { Lock, LockContext } from '@egr/wcf/modules/utils/async';
import { isNotNullOrEmpty, isNullOrEmpty } from '@egr/wcf/modules/utils/string';

// The `SharePayloadEntry` interface actually belongs in the `app-api' sub module,
// but this would be the only reason for the dependency to the `app-api'.
// Therefore it was moved into this file.
export interface SharePayloadEntry extends Partial<HashFileInfo> {
    action: 'fetch' | 'decode' | 'path';
    content: string;
    mimetype: string;
    filename: string;
    remove_path_after_share?: boolean;
}

export class ExportFailed extends CustomError {
    public constructor(public readonly exportFormat: GeometryExportFormatKey) {
        super(`Export failed: ${exportFormat}`);

        Object.setPrototypeOf(this, ExportFailed.prototype);
    }
}

export const enum GeometryExportCheck {
    NONE = 0,
    /** will abort export if not all articles are included */
    SILENT = 1,
    /** should show dialog where user can export even if not all articles will be included */
    WITH_QUESTION = 2
}

interface ExportPreCheckOptions {
    showMessage: boolean

    itemId?: string;
    expectedCount?: number;
    /** use for OBX export */
    allowUserArticleElements?: boolean;
}

export class ExportManager<App extends BaseApp = BaseApp> {
    /**
     * override for specific eaiws-setting on geometry exports
     */
    protected optionOverride(
        format: GeometryExportFormatKey,
        inputValue: EaiwsGeometryExportSetting
    ): EaiwsGeometryExportSetting {
        return inputValue;
    }

    protected imageOptionOverride(
        format: ImageExportFormatKey,
        inputValue: EaiwsImageExportSetting
    ): EaiwsImageExportSetting {
        return inputValue;
    }

    protected exportLock: Lock = new Lock();
    protected readonly deleteOptions: DeleteItemsOptions = new DeleteItemsOptions();
    protected readonly updateBasketArticleOptions: UpdateBasketArticlesOptions = new UpdateBasketArticlesOptions();

    public constructor(protected app: App) {
        this.startWebAR_Android = this.startWebAR_Android.bind(this);
        this.startWebAR_IOS = this.startWebAR_IOS.bind(this);

        this.deleteOptions.subItems = true;

        this.updateBasketArticleOptions.migrate = false;
        this.updateBasketArticleOptions.update = false;

        // register the callback used for creating the CAD content
        ShareManager.registerShareContent(
            // lookup all valid export keys and map them to `cadXXX` where `XXX`
            // is the key/upper case file extension
            // e.g. `cadDWG`
            Object.entries(GeometryExportFormat).map(entry => {
                const [key, value] = entry;
                if (value instanceof GeometryExportFormat) {
                    return `cad${key}`;
                }

                return;
            }).filter(isNotNullOrEmpty),
            this.shareExports
        );
    }

    public async getGeometryUrl(
        format: GeometryExportFormatKey,
        itemId?: string,
        check: GeometryExportCheck = GeometryExportCheck.NONE
    ): Promise<string | undefined> {
        if (check !== GeometryExportCheck.NONE) {
            const articleManager: ArticleManager = await this.app.sceneManager.getArticleManager();

            if (!(await this.exportPreCheck(articleManager, { itemId, showMessage: check === GeometryExportCheck.WITH_QUESTION }))) {
                return;
            }
        }

        const result: Array<string> | undefined = await this.getGeometryUrls([format], itemId ? [itemId] : undefined);
        return result != null ? result[0] : undefined;
    }

    public async getGeometryUrls(formats: Array<GeometryExportFormatKey>, itemIds?: ReadonlyArray<string>): Promise<Array<string> | undefined> {
        if (formats.filter((key: string) => !assertNotNull(GeometryExportFormat[key])).length) {
            return;
        }

        const [cleanupFunction, setId] = await this.prepareExport(itemIds);
        if (cleanupFunction == null || setId == null) {
            return undefined;
        }
        try {
            const result: Array<string> = [];
            for (const format of formats) {
                try {
                    const url: string = await this.app.eaiwsSession.basket.getExportedGeometry(
                        setId,
                        EaiwsGeometryExportSettingToOption(this.optionOverride(format, GeometryExportFormat[format].eaiwsSettings)),
                        120000
                    );
                    if (isNullOrEmpty(url)) {
                        throw new Error('Empty getExportedGeometry url');
                    }
                    result.push(url);
                } catch (error) {
                    captureException(
                        error,
                        'debug',
                        {
                            extra: {
                                exportFormat: format,
                            }
                        }
                    );
                    throw new ExportFailed(format);
                }
            }
            return result;
        } finally {
            cleanupFunction();
        }
    }

    public async getGeometryShareContentEntries(
        formats: Array<GeometryExportFormatKey>,
        filenameGenerator: (extension: string, format: string) => string,
        itemIds?: ReadonlyArray<string>
    ): Promise<Array<ShareContent & {format: GeometryExportFormatKey}>> {
        const payloads: Array<ShareContent & {format: GeometryExportFormatKey}> = [];
        const urls: Array<string> | undefined = await this.app.exportManager.getGeometryUrls(formats, itemIds);
        if (
            urls != null &&
            assert(urls.length === formats.length)
        ) {
            for (let i: number = 0; i < formats.length; i++) {
                const fileInfo: FileFormatInfo = FileFormats.get(formats[i])!;
                payloads.push({
                    format: formats[i],

                    source: ShareContentSource.REMOTE_URL,
                    content: urls[i],
                    filename: filenameGenerator(fileInfo.extension, formats[i]),
                    filetype: fileInfo
                });
            }
        }
        return payloads;
    }

    @withProgress('getImageUrl')
    public async getImageUrl(format: ImageExportFormatKey, itemId?: string, checkArticles: boolean = false, settings?: EaiwsImageExportSetting): Promise<string | undefined> {
        if (checkArticles) {
            const articleManager: ArticleManager = await this.app.sceneManager.getArticleManager();

            if (!(await this.exportPreCheck(articleManager, { showMessage: false }))) {
                return;
            }
        }

        const [cleanupFunction, setId] = await this.prepareExport(itemId ? [itemId] : []);

        if (cleanupFunction == null || setId == null) {
            return;
        }

        try {
            if (cleanupFunction == null || setId == null) {
                return;
            }
            const url: string = await this.app.eaiwsSession.basket.getGeneratedImage(
                setId,
                EaiwsImageExportSettingToOption(
                    settings ?? this.imageOptionOverride(format, ImageExportFormat[format].eaiwsSettings)
                ),
            );
            return url;
        } finally {
            cleanupFunction();
        }
    }

    @withProgress('startWebAR iOS')
    public async startWebAR_IOS(): Promise<void> {
        try {
            const usdExport: string | undefined = await this.getGeometryUrl('USDZ');

            if (isNullOrEmpty(usdExport)) {
                throw new Error('Export failed');
            }

            launchIOSQuicklook(usdExport);
        } catch (error) {
            this.app.messageManager.showMessage(i18next.t('Export failed'));
        }
    }

    @withProgress('startWebAR Android')
    public async startWebAR_Android(): Promise<void> {
        try {
            const glbExport: string | undefined = await this.getGeometryUrl('GLB');

            if (isNullOrEmpty(glbExport)) {
                throw new Error('Export failed');
            }

            launchAndroidModelViewer(glbExport);
        } catch (error) {
            this.app.messageManager.showMessage(i18next.t('Export failed'));
        }
    }

    /**
     * returns true if all articles will be included in export
     *
     * Warning: will update articles with unknown updateState if possible
     * @param articleManager
     */
    public async exportPreCheck(articleManager: ArticleManager, options?: ExportPreCheckOptions): Promise<boolean> {
        const exportableArticles: Array<MainArticleElement> = await this.getExportableMainArticles(articleManager, options?.itemId, options?.allowUserArticleElements);

        const check: number = options?.itemId != null ? 1 : options?.expectedCount ?? articleManager.getAllMainArticles().length;

         if (check === 0) {
            this.app.messageManager.showMessage(`${i18next.t('Export failed')} (${i18next.t('The product data available does not support the exporting of this article.', { count: check })})`);
            return false;
        }
        return (
            (exportableArticles.length === check) ||
            (options?.showMessage === true && (await this.showCADDownloadPrompt(exportableArticles.length, check)))
        );
    }

    @bind
    protected async *shareExports(shareTask: ShareTask, formats: ReadonlyArray<string>): AsyncIterableIterator<ShareContent> {
        if (shareTask.isCanceled) {
            return;
        }

        // Note: all format entries are like `cadXXX` where `XXX` is the actual
        // export format -> e.g. `cadDWG`
        const cadFormats = formats.map((value: string): GeometryExportFormatKey => value.substring(3) as unknown as GeometryExportFormatKey);
        const payloadEntries = await this.getGeometryShareContentEntries(
            cadFormats,
            (extension: string, format: string): string => shareTask.shareFilenameManager.getFilename(
                extension, 'cad_symbols', undefined, format === '3DS' ? '_3DS' : undefined
            ),
            shareTask.itemIds
        );

        for (const entry of payloadEntries) {
            const {format, ...payloadData} = entry;
            shareTask.telemetryEventQueue?.addPartialEvent({name: format});
            yield payloadData;
        }
    }

    protected async prepareExport(itemIds?: ReadonlyArray<string>): Promise<[VoidFunction, string, string | null] | [null, null, null]> {
        const context: LockContext = await this.exportLock.acquireLock();
        const alternateItems: Array<string> = [];

        const selectOptions: ItemSelectionOptions = new ItemSelectionOptions();
        selectOptions.wholeComposite = true;
        selectOptions.subItems = true;

        if (itemIds && itemIds?.length != 0) {
            // Note: we have to remove the `alternative position` flag before the item will be exported
            const selectOptions: ItemSelectionOptions = new ItemSelectionOptions();
            selectOptions.wholeComposite = true;
            selectOptions.subItems = true;

            for (const itemId of itemIds) {
                let properties: ItemProperties | undefined;

                const articleItem: ArticleItem | null = getArticleItem(this.app.eaiwsBasket, itemId);
                if (articleItem != null) {
                    properties = articleItem?.getCachedItemProperties() ?? await articleItem?.getItemProperties();
                } else {
                    const articleElement = this.app.sceneManager.getArticleManagerSync()?.getArticleById(itemId);
                    properties = articleElement?.getCachedItemProperties() ?? await articleElement?.getItemProperties();
                }

                const isAlternativePosition = properties?.article?.inactivePositionState !== undefined && properties?.article?.inactivePositionState.length !== 0;
                if (isAlternativePosition) {
                    alternateItems.push(itemId);
                }
            }
            await this.app.eaiwsSession.basket.changeAlternativePositionState(alternateItems, '', false, selectOptions);

            if (itemIds.length === 1) {
                return [
                    (): void => {
                        if (alternateItems.length != 0) {
                            void this.app.eaiwsSession.basket.changeAlternativePositionState(alternateItems, '', true, selectOptions);
                        }

                        context.unlock();
                    },
                    itemIds[0],
                    null
                ];
            }
        }
        if (itemIds?.length !== 1) {
            try {
                const articleManager: ArticleManager = await this.app.sceneManager.getArticleManager();
                const articles: Array<MainArticleElement> = await this.getExportableMainArticles(articleManager);

                if (articles.length === 0) {
                    context.unlock();
                    return [null, null, null];
                }

                const obxUrl: string = await articleManager.exportObx(articles);
                const folderId: string = await this.app.eaiwsSession.basket.insertFolder(null, null, 'Set');
                const setId: string = await this.app.eaiwsSession.basket.convertToSetArticle(folderId);
                const pastedIds: Array<string> = await this.app.eaiwsSession.basket.paste(setId, null, obxUrl);
                await this.app.eaiwsSession.basket.addToSetArticle(pastedIds);

                const cleanupFunction: VoidFunction = (): void => {
                    if (alternateItems.length != 0) {
                        void this.app.eaiwsSession.basket.changeAlternativePositionState(alternateItems, '', true, selectOptions);
                    }
                    // Note: it is not save to await the following promise
                    void this.app.eaiwsSession.basket.deleteItems([setId], this.deleteOptions).catch((error: Error) => captureException(error, 'debug'));

                    context.unlock();
                };
                return [cleanupFunction, setId, obxUrl];
            } catch (error) {
                context.unlock();

                throw error;
            }
        }
        return [null, null, null];
    }

    protected async getExportableMainArticles(articleManager: ArticleManager, itemId?: string, allowUserArticleElements: boolean = false): Promise<Array<MainArticleElement>> {
        const result: Array<MainArticleElement> = [];

        await articleManager.synchronizeSession(true);

        let articles: Array<MainArticleElement>;

        if (itemId != null) {
            const article: ArticleElement | null = articleManager.getArticleById(itemId);
            if (article == null || !(article instanceof MainArticleElement)) {
                return [];
            }
            articles = [article];
        } else {
            articles = articleManager.getAllMainArticles();
        }

        const unknownState: Map<string, MainArticleElement> = new Map();

        for (const article of articles) {
            if (allowUserArticleElements && article instanceof UserArticleElement && article.basketId != null) {
                result.push(article);
            } else if (!(article instanceof UserArticleElement) && article.basketId != null) {
                const state: OFMLUpdateState = article.getKnownOfmlUpdateState();
                switch (state) {
                    case 'Updatable':
                    case 'UpToDate':
                        result.push(article);
                        break;
                    case 'Unknown':
                        unknownState.set(article.basketId, article);
                        break;
                    default:
                        break;
                }
            }
        }

        const eaiwsStates: Array<UpdateBasketArticleResult> = await this.app.eaiwsSession.basket.updateBasketArticles(
            Array.from(unknownState.keys()), null, this.updateBasketArticleOptions
        );
        for (const state of eaiwsStates) {
            const article: MainArticleElement | undefined = unknownState.get(state.itemId);
            if (article == null) {
                continue;
            }

            if (state.updateState === 'UpToDate' || state.updateState === 'Updatable') {
                result.push(article);
            }
        }

        return result;
    }

    protected async showCADDownloadPrompt(articleCount: number, count: number): Promise<boolean> {
        if (articleCount === 0) {
            this.app.messageManager.showMessage(`${i18next.t('Export failed')} (${i18next.t('The product data available does not support the exporting of this article.', { count })})`);
            return false;
        }

        return this.app.dialogManager.showModalDialog(
            {
                title: i18next.t('Warning'),
                subTitle: [i18next.t('The export does not contain all visible items.')]
            },
            'CAD Download prompt'
        );
    }
}

/*
Used for options in share menus

(usdz, glb)
t('3D Model ({{type}})', { type })

(other geometries)
t('CAD-Model ({{type}})', { type })

*/